import { EmptyGuid } from "../emptyGuid";
import { luscinia } from "../luscinia";
import { Bootgrid } from "./bootgrid";
import { RecordPageViewApi } from "./ViewManager/ViewApi/RecordPageViewApi";
import { ViewManager } from "./ViewManager/ViewManager";
import { RecordPageView, ViewManagerView } from "./ViewManager/viewManagerTypes";

export default function initRecordPageViewManager(bootgrids: Bootgrid[], selectedViewId: string): ViewManager {
    const recordPageViewApi = new RecordPageViewApi();
    const recordPageViewManager = new ViewManager({
        addButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            luscinia.showConfirmationDialogWithExtraInput(
                "Add new view",
                "you are about to add new view please provide a name for the view ",
                async (viewNameInput) => {
                    const recordPageView = constructRecordPageView(viewNameInput, bootgrids);
                    const savedViewId = await recordPageViewApi.saveView(recordPageView);
                    await viewManager.refreshOptions();
                    viewManager.setSelectedViewId(savedViewId);
                }
            );
        },
        saveButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            const viewId = viewManager.getSelectedViewId();
            const currentViewName = viewManager.getSelectedViewName();
            if (viewId) {
                luscinia.showConfirmationDialog("Save view", `your changes will be saved to ${currentViewName}`, async () => {
                    const recordPageView = constructRecordPageView(currentViewName, bootgrids);
                    await recordPageViewApi.updateView(viewId, recordPageView);
                });
            }
        },
        editButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            luscinia.showConfirmationDialogWithExtraInput("Rename view", "please provide a new name for the view ", async (inputValue) => {
                const viewId = viewManager.getSelectedViewId();
                if (viewId) {
                    try {
                        await recordPageViewApi.updateViewName(viewId, inputValue);
                        await viewManager.refreshOptions();
                        viewManager.setSelectedViewId(viewId);
                    } catch (error) {
                        console.error(error);
                    }
                }
            });
        },
        shareButtonHandler(e, viewManager) {
            const viewId = viewManager.getSelectedViewId();
            if (viewId && viewId !== "default") {
                const view = viewManager.views.find((view) => view.Id === viewId);
                const setViewPrivate = async () => {
                    await recordPageViewApi.setViewPrivate(viewId, !view.IsPrivate);
                    await viewManager.refreshOptions();
                    viewManager.setSelectedViewId(viewId);
                };
                setViewPrivate();
            }
        },
        deleteButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            const viewId = viewManager.getSelectedViewId();
            if (viewId) {
                luscinia.showConfirmationDialog("Delete view", "you are about to delete a view are you sure?", async () => {
                    await recordPageViewApi.deleteView(viewId);
                    await viewManager.refreshOptions();
                    viewManager.setSelectedViewId("default");
                });
            }
        },
        select2ChangeHandler: async (viewManager: ViewManager) => {
            const selectedViewId = viewManager.getSelectedViewId();
            viewManager.views.map((view) => (view.IsActive = view.Id === selectedViewId));

            const selectedView = viewManager.views.find((view) => view.Id === selectedViewId);
            viewManager.updateButtonVisibilities(selectedView.ButtonVisibilities);
            viewManager.setIsPrivate(selectedView.IsPrivate);

            if (selectedViewId === "none") {
                return;
            }

            if (selectedViewId !== "default" && selectedViewId != null && selectedViewId != undefined) {
                const view = await recordPageViewApi.getView(selectedViewId);
                
                const bootgridIds = Object.keys(view.BootgridViews);
                const bootgridsToUpdate = bootgrids.filter(b => bootgridIds.includes(b.menuItemName));
                const mainBootgrid = bootgridsToUpdate.find(b => b.isMainBootgrid())

                if (mainBootgrid) {
                    await mainBootgrid.viewManager.setSelectedViewId(view.BootgridViews[mainBootgrid.menuItemName])
                }

                const subBootgrids = bootgridsToUpdate.filter(b => !b.isMainBootgrid())
                subBootgrids.forEach(bg => {
                    bg.viewManager.setSelectedViewId(view.BootgridViews[bg.menuItemName]);
                });
            }

            if (selectedViewId === "default") {
                const mainBootgrid = bootgrids.find(b => b.isMainBootgrid());
                await mainBootgrid.viewManager.setSelectedViewId("default");

                const subBootgrids = bootgrids.filter(b => !b.isMainBootgrid());
                subBootgrids.forEach((bootgrid) => {
                    bootgrid.viewManager.setSelectedViewId("default");
                });
            }
        },
        getViews: async () => {
            let pageViews: ViewManagerView[] = await recordPageViewApi.getViews(getPageName());
            const defaultViews: ViewManagerView[] = [
                {
                    Id: "default",
                    UserId: EmptyGuid.value,
                    Name: "Default",
                    DisplayName: "Default",
                    IsVisible: true,
                    IsActive: true,
                    IsPrivate: true,
                    IsDefault: true,
                    ButtonVisibilities: {
                        ShowEditButton: false,
                        ShowSaveButton: false,
                        ShowDeleteButton: false,
                        ShowShareButton: false,
                        ShowAddButton: true,
                    },
                },
                {
                    Id: "none",
                    UserId: EmptyGuid.value,
                    Name: "None",
                    DisplayName: "None",
                    IsVisible: true,
                    IsActive: false,
                    IsPrivate: true,
                    IsDefault: true,
                    ButtonVisibilities: {
                        ShowEditButton: false,
                        ShowSaveButton: false,
                        ShowDeleteButton: false,
                        ShowShareButton: false,
                        ShowAddButton: true,
                    },
                },
            ];

            pageViews.unshift(...defaultViews);

            pageViews.forEach((view) => {
                if (view.Name.startsWith("luscinia-temp-view-")) {
                    const originalViewId = view.Name.replace("luscinia-temp-view-", "");
                    const originalViewName = pageViews.find((v) => v.Id === originalViewId)?.Name;
                    view.DisplayName = originalViewName + "*";
                    if (originalViewId === "default") {
                        view.IsDefault = true;
                        view.ButtonVisibilities = {
                            ShowAddButton: true,
                            ShowDeleteButton: false,
                            ShowShareButton: false,
                            ShowEditButton: false,
                            ShowSaveButton: false,
                        };
                    } else {
                        view.ButtonVisibilities = {
                            ShowAddButton: true,
                            ShowDeleteButton: false,
                            ShowEditButton: false,
                            ShowShareButton: false,
                            ShowSaveButton: true,
                        };
                    }
                } else {
                    view.DisplayName = view.Name;
                    view.IsVisible = true;
                    if (!view.IsDefault) {
                        view.IsDefault = false;
                        view.ButtonVisibilities = {
                            ShowAddButton: true,
                            ShowDeleteButton: true,
                            ShowEditButton: true,
                            ShowSaveButton: true,
                            ShowShareButton: true,
                        };
                    }
                }
            });

            pageViews.map((v) => (v.IsActive = v.Id === selectedViewId));

            return pageViews;
        },
    });

    const viewManagerContainer = document.createElement("div");
    viewManagerContainer.classList.add("view-manager-container");
    document.querySelector(".page-head > div").insertAdjacentElement("beforeend", viewManagerContainer);
    recordPageViewManager.init(viewManagerContainer);

    return recordPageViewManager;
}

function constructRecordPageView(viewName: string, bootgrids: Bootgrid[]): RecordPageView {
    return {
        Id: EmptyGuid.value,
        UserId: EmptyGuid.value,
        ParentName: getPageName(),
        Name: viewName,
        IsPrivate: true,
        BootgridViews: bootgrids.reduce((acc, bootgrid) => {
            acc[bootgrid.menuItemName] = bootgrid.viewManager.getSelectedViewId();
            return acc;
        }, {}),
    };
}

function getPageName(): string {
    const bootgrid = document.querySelector('.luscinia-bootgrid') as HTMLTableElement;
    return bootgrid.getAttribute('data-luscinia-tablename');
}